import {
  createTheme,
  TextField,
  ThemeProvider,
  Typography,
} from "@mui/material";
import MuiTreeSelect, { FreeSoloNode } from "mui-tree-select";
import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import Button from "../Button";
import GlobalContext from "../GlobalContext";
import classes from "./index.module.css";

const theme = createTheme({
  components: {
    MuiTextField: {
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          return {
            height: ownerState?.allowMultiple ? "auto" : 34,
            maxHeight: 100,
            labelTop: -10,
            backgroundColor: "transparent !important",
            border: ownerState.error
              ? "none !important"
              : ownerState.variant === "outlined" &&
                `1px solid ${
                  ownerState.disabled ? "rgba(0, 0, 0, 0.25)" : "black"
                } !important`,
            borderBottom:
              ownerState.variant === "standard" && "1px solid black !important",
            borderTopRightRadius: 4,
            borderTopLeftRadius: 4,
            borderBottomRightRadius: ownerState.variant === "outlined" && 4,
            borderBottomLeftRadius: ownerState.variant === "outlined" && 4,
            // overflow: ownerState.variant !== "outlined" && "hidden",
            "& .MuiInputLabel-root": {
              color: ownerState.disabled ? "rgba(0,0,0,0.25)" : "black",
              // top: theme.size[ownerState.size].labelTop,
              "&.MuiInputLabel-shrink": {
                // overflow: "visible",
                background: "rgba(255, 255, 255, 1)",
                fontWeight: 700, //QA feedback
                lineHeight: "1em",
                letterSpacing: "0.06em",
                fontSize: 15,
                top: 0,
                padding:
                  ownerState.variant === "standard" ? "2px 5px" : "0px 5px",
                left: -5,
                "&.Mui-error": {
                  color: "#d32f2f",
                },
                textOverflow: "ellipsis",
              },
            },
            "& .MuiOutlinedInput-root": {
              "::-webkit-scrollbar": {
                display: "none",
              },
              maxHeight: 200,
              height: "auto",
              overflowX: "hidden",
              overflowY: "auto",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              background: ownerState.InputProps.readOnly ? "#a0a0a0" : "white",
              border: "none",
              "& .MuiOutlinedInput-notchedOutline": {
                // border: "1px solid black",
                border: "none",
              },
              "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                border: "1px solid #d32f2f",
              },
              "&.Mui-focused": {
                background: "#fdeba4",
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid #d32f2f",
                },
              },
            },
            ".Mui-focused": {
              background: "#fdeba4",
            },
          };
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            "& .MuiAutocomplete-input": {
              padding: 0,
            },
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-input": {
            height: "1.2em",
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          padding: "0 0 0 5px",
          "::after": {
            border: "none !important",
          },
          "::before": {
            border: "none !important",
          },
          "&.Mui-focused": {
            // background: "#fdeba4",
          },
          "&.Mui-error": {
            borderBottom: "1px solid #d32f2f",
          },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: ({ ownerState }) => {
          return {
            display: !ownerState["data-testid"] && "none",
          };
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          padding: "0 0 0 5px",
        },
        input: {
          padding: "0 4px", //QA feedback
          "::placeholder": {
            color: "#020002", //QA feedback
            fontSize: 15, //QA feedback
            fontWeight: 700, //QA feedback
            // textTransform: "capitalize",
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          padding: 0, //QA feedback
          color: "#020202",
          top: -10,
          fontWeight: "bold",
          "&.Mui-focused": {
            color: "black",
          },
          fontSize: 14,
          "& .MuiInputLabel-asterisk": {
            color: "red",
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          margin: 0,
          lineHeight: 1,
          fontWeight: 700,
          height: 13,
          width: "100%",
          padding: "0 14px",
          overflow: "hidden",
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          "& .MuiTypography-root": {
            letterSpacing: "0.00938em",
            lineHeight: 1.5,
            fontSize: "1rem",
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        divider: {
          "& .MuiListItemText-root": {
            "& .MuiTypography-root": {
              fontWeight: 700, // This is done for making header text bold.
            },
          },
        },
      },
    },
  },
});

/**
 * Do ref.current.getValue(value) before passing value props.
 */
const TreeSelect = forwardRef((props, ref) => {
  const {
    options = [],
    label,
    value,
    onChange = () => {},
    inputProps,
    required = false,
    error,
    showPath = true,
    allowSelectingBranch = true,
    allowMultiple = true,
  } = props;

  const [path, setPath] = useState("");
  const treeRef = useRef();

  const { createTreeSelectOptions } = useContext(GlobalContext);

  // const createOptions = useCallback((value, parent = null) => {
  //   return {
  //     value,
  //     parent,
  //     getParent() {
  //       return parent ? parent : null;
  //     },
  //     getChildren() {
  //       return (value.childs || []).map((child) => createOptions(child, this));
  //     },
  //     isBranch() {
  //       return !!value.childs;
  //     },
  //     isEqual(to) {
  //       return to.value?.id === value?.id;
  //     },
  //     toString() {
  //       return value?.name;
  //     },
  //   };
  // }, []);

  const findPath = useCallback((value, options = [], currentPath = "") => {
    const _v = value?.value;
    if (!_v) return null;
    for (let item of options) {
      if (item.id === _v.id) {
        return currentPath + item.name;
      } else if (item.childs) {
        const result = findPath(
          value,
          item.childs,
          currentPath + item.name + "/"
        );
        if (result) {
          return result;
        }
      }
    }
  }, []);

  useImperativeHandle(
    ref,
    () => {
      return {
        getValue: (value) => createTreeSelectOptions(value),
        getPath: () => path,
        scrollIntoView: () => {
          if (treeRef.current) {
            treeRef.current.scrollIntoView();
          }
        },
        getElement: () => treeRef.current,
      };
    },
    [createTreeSelectOptions, path, treeRef]
  );

  useEffect(() => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      setPath("");
      return;
    }

    if (allowMultiple) {
      const paths = value.map((val) => findPath(val, options)).filter(Boolean);
      setPath(paths.join(", "));
    } else {
      const path = findPath(value, options);
      setPath(path);
    }
  }, [findPath, options, value, allowMultiple]);

  const handleChange = (event, option) => {
    if (allowMultiple) {
      const newValue = value.includes(option)
        ? value.filter((v) => !v.isEqual(option))
        : [...value, option];
      onChange(event, newValue);
    } else {
      onChange(event, option);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <div className={classes["tree-select-container"]}>
        <MuiTreeSelect
          ref={treeRef}
          getChildren={(node) =>
            node
              ? node.getChildren()
              : (options || []).map((option) => createTreeSelectOptions(option))
          }
          getParent={(node) => {
            return node?.getParent ? node?.getParent() : null;
          }}
          isBranch={(node) => {
            return node?.isBranch();
          }}
          isOptionEqualToValue={(option, value) => {
            return option instanceof FreeSoloNode
              ? false
              : option.isEqual
              ? option.isEqual(value)
              : value;
          }}
          onChange={handleChange}
          value={value}
          multiple={allowMultiple}
          fullWidth
          {...props}
          renderInput={(params) => (
            <TextField
              label={label}
              required={required}
              error={error}
              allowMultiple={allowMultiple}
              onBlur={(e) => props?.onBlur?.(e)}
              {...params}
              inputProps={{ ...params.inputProps, ...inputProps }}
            />
          )}
          renderOption={(props, option, state) => {
            const isSelected =
              allowMultiple && value.some((v) => v.isEqual(option));
            return (
              // eslint-disable-next-line jsx-a11y/role-supports-aria-props
              <li
                {...props}
                className={[props.className, classes["options-conatiner"]]
                  .filter(Boolean)
                  .join(" ")}
                aria-disabled={isSelected}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    fontWeight: state?.pathDirection === "up" && 700,
                  }}
                >
                  {state.pathDirection === "up" && state.exitIcon}
                  {option.value.name}
                </div>
                <div className={classes["select-button"]}>
                  {allowSelectingBranch && state.pathDirection === "down" && (
                    <Button
                      color="violet"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isSelected) handleChange(e, option);
                      }}
                      label="Select"
                      disabled={isSelected}
                    />
                  )}
                </div>
                {state.pathDirection === "down" && state.enterIcon}
              </li>
            );
          }}
          slotProps={{
            popper: {
              className: classes["tree-select-popper"],
            },
          }}
        />
        {showPath && path && (
          <Typography
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            variant="caption"
            width={"100%"}
            overflow="hidden"
            title={path}
          >
            {path}
          </Typography>
        )}
      </div>
    </ThemeProvider>
  );
});

export default TreeSelect;
